import { useEffect, useState } from "react";
import { FileText, Download, Upload, Loader2, Trash2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Dokumen() {
  const [appDocs, setAppDocs] = useState([]);
  const [orgDocs, setOrgDocs] = useState([]);
  const [myDocs, setMyDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docName, setDocName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/applications/me"), api.get("/documents"), api.get("/members/me/profile")])
      .then(([a, d, p]) => {
        setAppDocs(a.data?.documents || []);
        setOrgDocs(d.data || []);
        setMyDocs(p.data?.documents || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const uploadDoc = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!docName.trim()) {
      toast.error("Sila masukkan nama dokumen dahulu");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload?is_public=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.post("/members/me/documents", { name: docName.trim(), file_id: up.data.file_id });
      setMyDocs(res.data.documents || []);
      setDocName("");
      toast.success("Dokumen berjaya dimuat naik");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const deleteDoc = async (index) => {
    setDeletingIndex(index);
    try {
      const res = await api.delete(`/members/me/documents/${index}`);
      setMyDocs(res.data.documents || []);
      toast.success("Dokumen dibuang");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeletingIndex(null);
    }
  };

  if (loading) return <div className="p-8 text-center text-piks-muted">Memuatkan dokumen...</div>;

  return (
    <div className="p-6 sm:p-10 max-w-2xl" data-testid="dokumen-page">
      <h1 className="font-display text-xl font-bold text-piks-black mb-6">Dokumen Saya</h1>

      <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Dokumen Lain (Sijil, dll.)</h2>
      <p className="text-xs text-piks-muted mb-3">Muat naik sijil latihan atau dokumen sokongan lain. Admin PPPS boleh melihat dokumen ini.</p>
      {myDocs.length === 0 ? (
        <p className="text-sm text-piks-muted mb-4" data-testid="dokumen-own-empty">Tiada dokumen lain dimuat naik.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {myDocs.map((d, i) => (
            <div key={i} className="flex items-center justify-between p-3.5 rounded-lg bg-white border border-piks-border" data-testid={`own-document-${i}`}>
              <a href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-sm text-piks-black hover:text-piks-gold flex-1 min-w-0">
                <FileText className="w-4 h-4 text-piks-muted shrink-0" /> <span className="truncate">{d.name}</span>
              </a>
              <button
                type="button"
                onClick={() => deleteDoc(i)}
                disabled={deletingIndex === i}
                data-testid={`own-document-delete-${i}`}
                className="text-piks-muted hover:text-piks-red shrink-0 ml-2"
              >
                {deletingIndex === i ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 mb-10">
        <Input
          value={docName}
          onChange={(e) => setDocName(e.target.value)}
          placeholder="Nama dokumen, contoh: Sijil Kursus Asas"
          data-testid="own-document-name-input"
          className="flex-1"
        />
        <Button type="button" variant="outline" disabled={uploading} className="relative shrink-0" data-testid="own-document-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Muat Naik
          <input
            type="file"
            accept="image/*,.pdf"
            disabled={uploading}
            onChange={uploadDoc}
            className="absolute inset-0 opacity-0 cursor-pointer"
            data-testid="own-document-file-input"
          />
        </Button>
      </div>

      <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Dokumen Permohonan</h2>
      {appDocs.length === 0 ? (
        <p className="text-sm text-piks-muted mb-8" data-testid="dokumen-application-empty">Tiada dokumen dimuat naik semasa permohonan.</p>
      ) : (
        <div className="space-y-2 mb-8">
          {appDocs.map((d, i) => (
            <a key={i} href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3.5 rounded-lg bg-white border border-piks-border hover:border-piks-gold/60" data-testid={`app-document-${i}`}>
              <span className="flex items-center gap-2.5 text-sm text-piks-black"><FileText className="w-4 h-4 text-piks-muted" /> {d.name}</span>
              <Download className="w-4 h-4 text-piks-muted" />
            </a>
          ))}
        </div>
      )}

      <h2 className="text-sm font-semibold text-piks-muted uppercase tracking-wide mb-3">Dokumen Rasmi PPPS</h2>
      {orgDocs.length === 0 ? (
        <p className="text-sm text-piks-muted" data-testid="dokumen-org-empty">Tiada dokumen rasmi tersedia.</p>
      ) : (
        <div className="space-y-2">
          {orgDocs.map((d) => (
            <a key={d.id} href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" className="flex items-center justify-between p-3.5 rounded-lg bg-white border border-piks-border hover:border-piks-gold/60" data-testid={`org-document-${d.id}`}>
              <span className="flex items-center gap-2.5 text-sm text-piks-black"><FileText className="w-4 h-4 text-piks-muted" /> {d.title}</span>
              <Download className="w-4 h-4 text-piks-muted" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
